// src/analytics.ts
import ReactGA from "react-ga4";

/* Analytics integrity guardrail.
   GA4 must never receive traffic from our own automated verification passes.
   Tracking is skipped when:
     1. the URL carries ?noga=1 (also remembered in localStorage for the whole
        browser profile, so every later navigation in that automated session
        stays untracked) — this is the flag all internal QA runs must use;
     2. the page is rendered inside an iframe (our mobile viewport audits load
        the site in a 390x844 same-origin iframe; real visitors do not);
     3. the browser announces itself as automated (navigator.webdriver) or has
        a headless user agent;
     4. we are running on localhost / the local preview tunnel. */
export const analyticsBlocked = (): boolean => {
  try {
    if (typeof window === "undefined") return true;
    const q = window.location.search || "";
    if (/[?&]noga=1/.test(q)) {
      try {
        window.localStorage.setItem("sd_noga", "1");
      } catch (e) {
        /* ignore */
      }
      return true;
    }
    try {
      if (window.localStorage.getItem("sd_noga") === "1") return true;
    } catch (e) {
      /* ignore */
    }
    if (window.top !== window.self) return true;
    const nav = window.navigator as Navigator & { webdriver?: boolean };
    if (nav.webdriver) return true;
    if (/headless|puppeteer|playwright|phantom|bot|crawler|spider/i.test(nav.userAgent || "")) {
      return true;
    }
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0") return true;
    return false;
  } catch (e) {
    return false;
  }
};

let enabled = false;

export const initGA = (trackingId: string) => {
  if (analyticsBlocked()) {
    enabled = false;
    return;
  }
  enabled = true;
  ReactGA.initialize(trackingId);
};

export const logPageView = () => {
  if (!enabled) return;
  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname + window.location.search,
  });
};

export const logEvent = (category: string, action: string, label?: string) => {
  if (!enabled) return;
  ReactGA.event({ category, action, label });
};
